import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import type { Case, CaseDocument, Client } from '@core/domain/model';
import {
  birthDateLine,
  initialsOf,
  toDocumentRow,
  type ClientCaseRow,
  type ClientDocumentRow,
} from './formatter';

export interface ClientDetailState {
  isLoading: boolean;
  notFound: boolean;
  name: string;
  initials: string;
  phone: string | null;
  email: string | null;
  /** «14 августа 1978 · 47 лет» либо `null`. */
  birthLine: string | null;
  birthDate: string | null;
  address: string | null;
  notes: string | null;
  /** Активные дела идут первыми — «Структура» §8.2 показывает их отдельной группой. */
  activeCases: ClientCaseRow[];
  closedCases: ClientCaseRow[];
  /** Документы, привязанные к самому клиенту, а не к делу — «Структура» §8.3. */
  documents: ClientDocumentRow[];
  isDeleted: boolean;
}

const TYPE_LABEL: Record<Case['type'], string> = {
  Consultation: 'Консультация',
  ClaimWork: 'Претензионная работа',
  CourtCase: 'Судебное дело',
  Enforcement: 'Исп. производство',
  Other: 'Другое',
};

const toCaseRow = (item: Case): ClientCaseRow => ({
  id: item.id,
  title: item.title,
  subtitle: item.type === 'Other' && item.typeOther?.trim() ? item.typeOther : TYPE_LABEL[item.type],
  isActive: item.status === 'Active',
});

/**
 * Карточка клиента — «Структура» §8.
 *
 * Здесь всё, что о человеке знает приложение: контакты, его дела, документы, заметки.
 * Звонок и письмо — системными средствами телефона (`tel:` и `mailto:`), своего
 * набора номера мы не делаем.
 */
export function useClientDetail(clientId: string, db: Repositories, now: () => number = Date.now) {
  const [client, setClient] = useState<Client | null>(null);
  const [cases, setCases] = useState<Case[]>([]);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDeleted, setDeleted] = useState(false);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const found = await db.clients.find(clientId);
      if (dropped) return;

      if (!found) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const [allCases, ofClient] = await Promise.all([
        db.cases.list({ clientId }),
        db.documents.ofClient(clientId),
      ]);
      if (dropped) return;

      setClient(found);
      setCases(allCases);
      setDocuments(ofClient);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, clientId, version]);

  const state: ClientDetailState = useMemo(
    () => ({
      isLoading,
      notFound,
      name: client?.fullName ?? '',
      initials: client ? initialsOf(client.fullName) : '?',
      phone: client?.phone ?? null,
      email: client?.email ?? null,
      birthLine: client?.birthDate ? birthDateLine(client.birthDate, now()) : null,
      birthDate: client?.birthDate ?? null,
      address: client?.address ?? null,
      notes: client?.notes ?? null,
      activeCases: cases.filter((item) => item.status !== 'Completed').map(toCaseRow),
      closedCases: cases.filter((item) => item.status === 'Completed').map(toCaseRow),
      documents: documents.map(toDocumentRow),
      isDeleted,
    }),
    [client, cases, documents, isLoading, notFound, isDeleted, now],
  );

  const edit = useCallback(
    async (change: (current: Client) => Client) => {
      if (!client) return;

      await db.clients.update(change(client));
      refresh();
    },
    [db, client, refresh],
  );

  const actions = useMemo(
    () => ({
      setName: (fullName: string) =>
        fullName.trim().length > 0 ? void edit((current) => ({ ...current, fullName })) : undefined,

      setPhone: (phone: string) => void edit((current) => ({ ...current, phone: phone.trim() || null })),
      setEmail: (email: string) => void edit((current) => ({ ...current, email: email.trim() || null })),
      setAddress: (address: string) =>
        void edit((current) => ({ ...current, address: address.trim() || null })),
      setNotes: (notes: string) => void edit((current) => ({ ...current, notes: notes.trim() || null })),
      setBirthDate: (birthDate: string | null) =>
        void edit((current) => ({ ...current, birthDate: birthDate || null })),

      async remove() {
        if (!client) return;

        await db.clients.remove(client.id);
        setDeleted(true);
      },

      async undoDelete() {
        if (!client) return;

        await db.clients.restore(client.id);
        setDeleted(false);
        refresh();
      },

      deletionShown: () => setDeleted(false),

      /** Заводит дело прямо из карточки клиента: так его чаще всего и заводят. */
      async createCase(title: string) {
        if (!client || title.trim().length === 0) return null;

        const created = await db.cases.create({
          id: '',
          title: title.trim(),
          clientId: client.id,
          type: 'Other',
          status: 'Active',
          typeOther: null,
          description: null,
          notes: null,
          links: [],
          isPinned: false,
          sortOrder: 0,
        });
        refresh();

        return created;
      },

      refresh,
    }),
    [db, client, edit, refresh],
  );

  return { state, actions };
}

export type ClientDetailActions = ReturnType<typeof useClientDetail>['actions'];
